<template>
  <v-card class="profile-edit-page" dark flat>
    <v-card-text class="profile-edit-page__content">
      <v-container class="profile-edit-shell px-4 px-sm-6 py-6 py-sm-8">
        <section class="profile-edit-hero" aria-labelledby="profile-edit-title">
          <div class="profile-edit-hero__icon" aria-hidden="true">
            <v-icon color="#7ee5a6" size="30">mdi-account-edit-outline</v-icon>
          </div>
          <div class="profile-edit-hero__copy">
            <div class="profile-edit-eyebrow">Mein Profil</div>
            <h1 id="profile-edit-title">Deine Daten, immer aktuell</h1>
            <p>Prüfe deine persönlichen Angaben und speichere Änderungen mit einem Klick.</p>
          </div>
        </section>

        <div v-if="!user" class="profile-edit-loading" role="status" aria-live="polite">
          <v-progress-circular indeterminate color="primary" size="42" width="4"></v-progress-circular>
          <span>Profildaten werden geladen</span>
        </div>

        <v-form v-else ref="accountForm" class="profile-edit-form" lazy-validation novalidate @submit.prevent="saveProfile">
          <section class="profile-edit-section" aria-labelledby="personal-data-title">
            <div class="profile-edit-section__header">
              <div class="profile-edit-section__icon" aria-hidden="true">
                <v-icon color="#7ee5a6" size="22">mdi-account-outline</v-icon>
              </div>
              <div>
                <h2 id="personal-data-title">Persönliche Angaben</h2>
                <p>So kennen wir dich in deinem Studio.</p>
              </div>
            </div>

            <v-row class="profile-edit-grid" dense>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="user.vorname"
                  class="profile-field"
                  label="Vorname"
                  autocomplete="given-name"
                  outlined
                  rounded
                  hide-details="auto"
                  required></v-text-field>
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="user.nachname"
                  class="profile-field"
                  label="Nachname"
                  autocomplete="family-name"
                  outlined
                  rounded
                  hide-details="auto"
                  required></v-text-field>
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="user.groesse"
                  class="profile-field"
                  type="number"
                  inputmode="numeric"
                  label="Grösse"
                  suffix="cm"
                  min="80"
                  max="250"
                  outlined
                  rounded
                  hide-details="auto"
                  required></v-text-field>
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="user.geburtstag"
                  class="profile-field"
                  type="date"
                  label="Geburtstag"
                  autocomplete="bday"
                  outlined
                  rounded
                  hide-details="auto"
                  required></v-text-field>
              </v-col>
            </v-row>
          </section>

          <section class="profile-edit-section" aria-labelledby="contact-data-title">
            <div class="profile-edit-section__header">
              <div class="profile-edit-section__icon" aria-hidden="true">
                <v-icon color="#7ee5a6" size="22">mdi-email-outline</v-icon>
              </div>
              <div>
                <h2 id="contact-data-title">Kontakt</h2>
                <p>Damit dich dein Studio zuverlässig erreichen kann.</p>
              </div>
            </div>

            <v-row class="profile-edit-grid" dense>
              <v-col cols="12" sm="7">
                <v-text-field
                  v-model="user.email"
                  class="profile-field"
                  type="email"
                  label="E-Mail-Adresse"
                  autocomplete="email"
                  :loading="validatingEmail === true"
                  :rules="rules.email"
                  :error-messages="emailAlreadyUsed ? 'Diese E-Mail-Adresse ist bereits vergeben' : ''"
                  validate-on-blur
                  outlined
                  rounded
                  hide-details="auto"
                  required
                  @input="resetEmailValidation"
                  @blur="validateEmail"></v-text-field>
              </v-col>
              <v-col cols="12" sm="5">
                <v-text-field
                  v-model="user.telefon"
                  class="profile-field"
                  type="tel"
                  label="Telefon"
                  autocomplete="tel"
                  :rules="rules.phone"
                  validate-on-blur
                  outlined
                  rounded
                  hide-details="auto"
                  required></v-text-field>
              </v-col>
            </v-row>
          </section>

          <section class="profile-edit-section" aria-labelledby="address-data-title">
            <div class="profile-edit-section__header">
              <div class="profile-edit-section__icon" aria-hidden="true">
                <v-icon color="#7ee5a6" size="22">mdi-map-marker-outline</v-icon>
              </div>
              <div>
                <h2 id="address-data-title">Adresse</h2>
                <p>Deine aktuelle Wohn- und Korrespondenzadresse.</p>
              </div>
            </div>

            <v-row class="profile-edit-grid" dense>
              <v-col cols="12">
                <v-text-field
                  v-model="user.adresse_1"
                  class="profile-field"
                  label="Strasse und Hausnummer"
                  autocomplete="street-address"
                  outlined
                  rounded
                  hide-details="auto"
                  required></v-text-field>
              </v-col>
              <v-col cols="12" sm="4">
                <v-text-field
                  v-model="user.plz"
                  class="profile-field"
                  label="PLZ"
                  autocomplete="postal-code"
                  inputmode="numeric"
                  :rules="rules.isNumber"
                  validate-on-blur
                  outlined
                  rounded
                  hide-details="auto"
                  required></v-text-field>
              </v-col>
              <v-col cols="12" sm="8">
                <v-text-field
                  v-model="user.stadt"
                  class="profile-field"
                  label="Ort"
                  autocomplete="address-level2"
                  outlined
                  rounded
                  hide-details="auto"
                  required></v-text-field>
              </v-col>
              <v-col cols="12">
                <v-text-field
                  v-model="user.land"
                  class="profile-field"
                  label="Land"
                  autocomplete="country-name"
                  outlined
                  rounded
                  hide-details="auto"
                  required></v-text-field>
              </v-col>
            </v-row>
          </section>

          <section v-if="user.studio?.parkingAvailable" class="profile-edit-section" aria-labelledby="vehicle-data-title">
            <div class="profile-edit-section__header profile-edit-section__header--action">
              <div class="profile-edit-section__heading">
                <div class="profile-edit-section__icon" aria-hidden="true">
                  <v-icon color="#7ee5a6" size="22">mdi-car-outline</v-icon>
                </div>
                <div>
                  <h2 id="vehicle-data-title">Fahrzeuge</h2>
                  <p>Für die Parkberechtigung in deinem Center.</p>
                </div>
              </div>
              <span class="profile-edit-count">{{ user.licensePlates.length }} / 10</span>
            </div>

            <div class="profile-edit-vehicles">
              <div v-for="(plate, index) in user.licensePlates" :key="index" class="profile-edit-vehicle">
                <v-text-field
                  v-model="user.licensePlates[index]"
                  class="profile-field flex-grow-1"
                  :label="index === 0 ? 'Kennzeichen 1' : 'Kennzeichen ' + (index + 1) + ' (optional)'"
                  :placeholder="index === 0 ? 'z. B. ZH 12345' : ''"
                  autocomplete="off"
                  prepend-inner-icon="mdi-car"
                  outlined
                  rounded
                  hide-details="auto"></v-text-field>
                <v-btn
                  v-if="user.licensePlates.length > 1"
                  class="profile-edit-vehicle__delete"
                  color="error"
                  icon
                  type="button"
                  :aria-label="'Kennzeichen ' + (index + 1) + ' entfernen'"
                  @click="removeLicensePlate(index)">
                  <v-icon size="22">mdi-delete-outline</v-icon>
                </v-btn>
              </div>
            </div>

            <v-btn
              class="profile-edit-add-button mt-4"
              color="primary"
              outlined
              type="button"
              :disabled="user.licensePlates.length >= 10"
              @click="addLicensePlate">
              <v-icon left size="20">mdi-plus</v-icon>
              Kennzeichen hinzufügen
            </v-btn>
          </section>

          <section class="profile-edit-save" aria-live="polite">
            <div class="profile-edit-save__copy">
              <div class="profile-edit-save__status" :class="{ 'profile-edit-save__status--changed': hasChanges }">
                <v-icon :color="hasChanges ? '#7ee5a6' : '#9ba39f'" size="18">
                  {{ hasChanges ? "mdi-circle-medium" : "mdi-check-circle-outline" }}
                </v-icon>
                {{ hasChanges ? "Änderungen noch nicht gespeichert" : "Alle Angaben sind aktuell" }}
              </div>
              <p>Nach dem Speichern gelangst du zurück zu deinem Profil.</p>
            </div>
            <v-btn
              class="profile-edit-save__button"
              color="primary"
              depressed
              x-large
              type="submit"
              :loading="saving"
              :disabled="!hasChanges || validatingEmail === true">
              <v-icon left size="22">mdi-content-save-outline</v-icon>
              Änderungen speichern
            </v-btn>
          </section>

          <section class="profile-edit-danger" aria-labelledby="delete-account-title">
            <div>
              <h2 id="delete-account-title">Account löschen</h2>
              <p>Dein Account und die zugehörigen persönlichen Daten werden dauerhaft entfernt.</p>
            </div>
            <v-btn class="profile-edit-danger__button" color="error" outlined type="button" :loading="deleting" @click="deleteUserAccount">
              <v-icon left size="20">mdi-delete-outline</v-icon>
              Account löschen
            </v-btn>
          </section>
        </v-form>
      </v-container>
    </v-card-text>
  </v-card>
</template>

<script>
import { mapActions, mapState } from "vuex"
import api from "@/api"

export default {
  name: "UserEditProfile",
  data() {
    return {
      user: null,
      userBackup: null,
      saving: false,
      deleting: false,
      validatingEmail: false,
      emailAlreadyUsed: false,
      rules: {
        phone: [(value) => /^(\+?\d{1,4}\s?){0,4}\d{1,10}$/.test(value) || "Bitte gib eine gültige Telefonnummer ein"],
        email: [
          (value) => !!value || "Die E-Mail-Adresse wird benötigt",
          (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) || "Bitte gib eine gültige E-Mail-Adresse ein",
        ],
        isNumber: [(value) => !!value || "Dieses Feld wird benötigt.", (value) => /^\d+$/.test(value) || "Bitte gib eine Zahl ein."],
      },
    }
  },
  mounted() {
    if (this.navigationVisible) this.toggleNavigationBarVisibility()
  },
  beforeDestroy() {
    if (!this.navigationVisible) this.toggleNavigationBarVisibility()
  },
  beforeRouteLeave(to, from, next) {
    if (!this.hasChanges || this.saving) {
      next()
      return
    }

    next(window.confirm("Du hast ungespeicherte Änderungen. Möchtest du sie wirklich verwerfen?"))
  },
  created() {
    this.fetchUser()
  },
  methods: {
    ...mapActions("navigationStoreNotPersisted", ["toggleNavigationBarVisibility"]),
    resetEmailValidation() {
      this.validatingEmail = false
      this.emailAlreadyUsed = false
    },
    async validateEmail() {
      if (!this.user || !this.userBackup || !this.$refs.accountForm) return false
      if (!this.$refs.accountForm.validate()) return false

      if (this.user.email.toLowerCase() === this.userBackup.email.toLowerCase()) {
        this.validatingEmail = "validated"
        this.emailAlreadyUsed = false
        return true
      }

      this.validatingEmail = true

      try {
        this.emailAlreadyUsed = await api.getEmailAlreadyUsed({ email: this.user.email })
        this.validatingEmail = this.emailAlreadyUsed ? false : "validated"
        return !this.emailAlreadyUsed
      } catch (error) {
        this.validatingEmail = false
        this.$toast.error("Die E-Mail-Adresse konnte nicht geprüft werden")
        return false
      }
    },
    async saveProfile() {
      if (!this.hasChanges || this.saving) return

      this.saving = true

      try {
        const emailIsValid = await this.validateEmail()
        const formIsValid = this.$refs.accountForm.validate()

        if (!emailIsValid || !formIsValid) {
          this.$toast.error("Bitte überprüfe die Eingaben")
          return
        }

        this.user.email = this.user.email.toLowerCase()
        this.userBackup.email = this.userBackup.email.toLowerCase()

        await api.editUserProfile({ newUser: this.user, oldUser: this.userBackup })

        const platesToSave = this.user.licensePlates.filter((plate) => plate && plate.trim() !== "")
        await api.updateLicensePlates(platesToSave)

        localStorage.removeItem("user")

        const localUser = {
          _id: this.userBackup._id,
          email: this.user.email,
          vorname: this.user.vorname,
          nachname: this.user.nachname,
          studio_id: this.user.studio_id,
        }

        localStorage.setItem("user", JSON.stringify(localUser))
        this.userBackup = JSON.parse(JSON.stringify(this.user))
        this.$toast.success("Profil gespeichert")
        this.$router.push("/user")
      } catch (error) {
        this.$toast.error("Fehler beim Speichern")
      } finally {
        this.saving = false
      }
    },
    async fetchUser() {
      try {
        const response = await api.getUser()
        const user = response.user

        if (!user.licensePlates || user.licensePlates.length === 0) {
          user.licensePlates = [""]
        }

        this.user = user
        this.userBackup = JSON.parse(JSON.stringify(user))
      } catch (error) {
        this.$toast.error("Fehler beim Laden")
      }
    },
    addLicensePlate() {
      if (this.user.licensePlates.length < 10) {
        this.user.licensePlates.push("")
      }
    },
    removeLicensePlate(index) {
      if (this.user.licensePlates.length > 1) {
        this.user.licensePlates.splice(index, 1)
      }
    },
    async deleteUserAccount() {
      if (!window.confirm("Möchtest du deinen Account wirklich dauerhaft löschen?")) return

      this.deleting = true

      try {
        await api.deleteOwnAccount()
        localStorage.removeItem("token")
        localStorage.removeItem("user")
        location.reload()
      } catch (error) {
        this.$toast.error("Der Account konnte nicht gelöscht werden")
      } finally {
        this.deleting = false
      }
    },
  },
  computed: {
    ...mapState("navigationStoreNotPersisted", ["navigationVisible"]),
    hasChanges() {
      if (!this.user || !this.userBackup) return false
      return JSON.stringify(this.user) !== JSON.stringify(this.userBackup)
    },
  },
}
</script>

<style lang="scss" scoped>
.profile-edit-page {
  min-height: calc(100vh - 5rem);
  min-height: calc(100dvh - 5rem);
  background: radial-gradient(circle at 8% 0%, rgba(0, 150, 63, 0.17), transparent 24rem), linear-gradient(180deg, #090c0a 0%, #111411 100%) !important;
}

.profile-edit-page__content {
  padding: 0 !important;
  color: #ffffff !important;
  font-size: 1rem;
}

.profile-edit-shell {
  width: 100%;
  max-width: 58rem;
  margin: 0 auto;
  padding-bottom: 7rem !important;
}

.profile-edit-hero {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.5rem;
  margin-bottom: 1rem;
  overflow: hidden;
  border: 1px solid rgba(126, 229, 166, 0.22);
  border-radius: 1.5rem;
  background: linear-gradient(125deg, rgba(0, 150, 63, 0.18), rgba(21, 27, 23, 0.92)), #121613;
  box-shadow: 0 1.25rem 3rem rgba(0, 0, 0, 0.18);
}

.profile-edit-hero__icon,
.profile-edit-section__icon {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(126, 229, 166, 0.2);
  background: rgba(0, 150, 63, 0.13);
}

.profile-edit-hero__icon {
  width: 3.75rem;
  height: 3.75rem;
  border-radius: 1.125rem;
}

.profile-edit-hero__copy {
  min-width: 0;
}

.profile-edit-eyebrow {
  margin-bottom: 0.25rem;
  color: #7ee5a6;
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.09rem;
  line-height: 1.4;
  text-transform: uppercase;
}

.profile-edit-hero h1 {
  margin: 0;
  color: #ffffff;
  font-size: clamp(1.625rem, 4vw, 2.125rem);
  font-weight: 850;
  line-height: 1.15;
}

.profile-edit-hero p,
.profile-edit-section__header p,
.profile-edit-save p,
.profile-edit-danger p {
  margin: 0.35rem 0 0;
  color: #aeb7b2;
  font-size: 0.875rem;
  line-height: 1.55;
}

.profile-edit-loading {
  display: flex;
  min-height: 18rem;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  color: #c8cfcb;
  font-size: 1rem;
}

.profile-edit-form {
  display: grid;
  gap: 1rem;
}

.profile-edit-section {
  padding: 1.5rem;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 1.25rem;
  background: rgba(20, 24, 21, 0.96);
  box-shadow: 0 0.875rem 2.25rem rgba(0, 0, 0, 0.14);
}

.profile-edit-section__header,
.profile-edit-section__heading {
  display: flex;
  align-items: center;
  gap: 0.875rem;
}

.profile-edit-section__header {
  margin-bottom: 1.25rem;
}

.profile-edit-section__header--action {
  justify-content: space-between;
}

.profile-edit-section__icon {
  width: 2.75rem;
  height: 2.75rem;
  border-radius: 0.875rem;
}

.profile-edit-section h2,
.profile-edit-danger h2 {
  margin: 0;
  color: #ffffff;
  font-size: 1.125rem;
  font-weight: 800;
  line-height: 1.3;
}

.profile-edit-grid {
  margin: -0.375rem;
}

.profile-edit-grid > .col,
.profile-edit-grid > [class*="col-"] {
  padding: 0.375rem;
}

.profile-edit-count {
  flex: 0 0 auto;
  padding: 0.35rem 0.65rem;
  border: 1px solid rgba(126, 229, 166, 0.2);
  border-radius: 999px;
  color: #7ee5a6;
  background: rgba(0, 150, 63, 0.1);
  font-size: 0.75rem;
  font-weight: 800;
  line-height: 1.3;
}

.profile-edit-vehicles {
  display: grid;
  gap: 0.75rem;
}

.profile-edit-vehicle {
  display: flex;
  align-items: flex-start;
  gap: 0.625rem;
}

.profile-edit-vehicle__delete {
  width: 3.25rem !important;
  height: 3.25rem !important;
  margin-top: 0.2rem;
  border: 1px solid rgba(255, 82, 82, 0.28);
  background: rgba(255, 82, 82, 0.08);
}

.profile-edit-add-button,
.profile-edit-danger__button {
  min-height: 2.75rem;
  border-radius: 0.875rem;
  font-size: 0.875rem;
  font-weight: 750;
  letter-spacing: 0;
  text-transform: none;
}

.profile-edit-save {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.25rem;
  padding: 1.25rem;
  border: 1px solid rgba(126, 229, 166, 0.24);
  border-radius: 1.25rem;
  background: linear-gradient(120deg, rgba(0, 150, 63, 0.15), rgba(17, 22, 19, 0.98));
  box-shadow: 0 1rem 2.5rem rgba(0, 0, 0, 0.18);
}

.profile-edit-save__copy {
  min-width: 0;
}

.profile-edit-save__status {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  color: #b2bbb6;
  font-size: 0.875rem;
  font-weight: 800;
  line-height: 1.4;
}

.profile-edit-save__status--changed {
  color: #ffffff;
}

.profile-edit-save__button {
  min-width: 15rem;
  min-height: 3.25rem !important;
  flex: 0 0 auto;
  border-radius: 1rem;
  font-size: 1rem;
  font-weight: 800;
  letter-spacing: 0;
  text-transform: none;
  box-shadow: 0 0.75rem 1.75rem rgba(0, 150, 63, 0.24);
}

.profile-edit-danger {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.25rem;
  padding: 1.25rem 0.25rem 0;
}

::v-deep .profile-field.v-text-field--outlined > .v-input__control > .v-input__slot {
  min-height: 3.5rem;
  border-radius: 1rem;
  background: rgba(7, 10, 8, 0.55);
}

::v-deep .profile-field.v-text-field--outlined fieldset {
  border-color: rgba(255, 255, 255, 0.14);
}

::v-deep .profile-field.v-text-field--outlined:hover fieldset {
  border-color: rgba(126, 229, 166, 0.44);
}

::v-deep .profile-field input,
::v-deep .profile-field .v-label,
::v-deep .profile-field .v-text-field__suffix {
  font-size: 1rem;
}

::v-deep .profile-field .v-messages {
  font-size: 0.75rem;
  line-height: 1.4;
}

::v-deep .profile-field .v-icon {
  color: #8d9891;
}

@media (max-width: 37.5rem) {
  .profile-edit-shell {
    padding-bottom: 6rem !important;
  }

  .profile-edit-hero {
    align-items: flex-start;
    padding: 1.125rem;
    border-radius: 1.25rem;
  }

  .profile-edit-hero__icon {
    width: 3.25rem;
    height: 3.25rem;
  }

  .profile-edit-section {
    padding: 1.125rem;
    border-radius: 1.125rem;
  }

  .profile-edit-save,
  .profile-edit-danger {
    flex-direction: column;
    align-items: stretch;
  }

  .profile-edit-save__button,
  .profile-edit-danger__button {
    width: 100%;
  }
}
</style>
